import { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AnswerEditor } from './answer-editor';
import { SlackPreview } from './slack-preview';
import { formatGoldenAnswerPreview, createGoldenAnswer } from '@/lib/api';
import type { Conversation } from '@/lib/api';
import type { SlackBlock, Citation } from '@/lib/slack-styles';
import { Check, Loader2, Sparkles, AlertCircle } from 'lucide-react';

interface GoldenAnswerEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversation: Conversation;
  initialScore: number;
  onSuccess: () => void;
}

/**
 * Two-panel dialog for editing and previewing golden answers
 * Left: Editor with citation toolbar
 * Right: Pixel-perfect Slack preview
 */
export function GoldenAnswerEditorDialog({
  open,
  onOpenChange,
  conversation,
  initialScore,
  onSuccess,
}: GoldenAnswerEditorDialogProps) {
  // Editor state
  const [rawAnswer, setRawAnswer] = useState('');
  const [citations, setCitations] = useState<Citation[]>([]);

  // Preview state
  const [formattedText, setFormattedText] = useState('');
  const [blocks, setBlocks] = useState<SlackBlock[]>([]);
  const [isFormatting, setIsFormatting] = useState(false);
  const [formatError, setFormatError] = useState<string | null>(null);

  // Debounce timer
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Initialize with AI response
  useEffect(() => {
    if (open && conversation.response) {
      setRawAnswer(conversation.response);
    }
  }, [open, conversation.response]);

  // Debounced format preview
  const fetchFormatPreview = useCallback(async (text: string) => {
    if (!text.trim()) {
      setBlocks([]);
      setFormattedText('');
      setFormatError(null);
      return;
    }

    setIsFormatting(true);
    setFormatError(null);

    try {
      const result = await formatGoldenAnswerPreview({
        rawAnswer: text,
        query: conversation.query,
      });

      setFormattedText(result.formattedText);
      setBlocks(result.blocks);
      setCitations(result.extractedCitations);
    } catch (error) {
      console.error('Format preview error:', error);
      setFormatError(error instanceof Error ? error.message : 'Failed to format preview');
    } finally {
      setIsFormatting(false);
    }
  }, [conversation.query]);

  // Handle input changes with debounce
  const handleAnswerChange = (value: string) => {
    setRawAnswer(value);

    // Extract citations immediately for UI
    const citationRegex = /\[([^\]]+):\s*(https?:\/\/[^\]]+)\]/g;
    const extractedCitations: Citation[] = [];
    let match;
    while ((match = citationRegex.exec(value)) !== null) {
      extractedCitations.push({
        label: match[1],
        url: match[2],
        position: match.index,
      });
    }
    setCitations(extractedCitations);

    // Debounce the format preview API call
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    const timer = setTimeout(() => {
      fetchFormatPreview(value);
    }, 500);
    setDebounceTimer(timer);
  };

  // Create golden answer mutation
  const createMutation = useMutation({
    mutationFn: async () => {
      if (!formattedText) {
        throw new Error('Please wait for formatting to complete');
      }

      return createGoldenAnswer({
        conversationId: conversation.id,
        query: conversation.query,
        approvedResponse: formattedText,
        rawResponse: rawAnswer,
        formattedResponse: formattedText,
        citations,
        isAdminEdited: true,
        evalScore: initialScore,
      });
    },
    onSuccess: () => {
      onSuccess();
      onOpenChange(false);
    },
  });

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      setRawAnswer('');
      setFormattedText('');
      setBlocks([]);
      setCitations([]);
      setFormatError(null);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    }
  }, [open, debounceTimer]);

  const canSave = rawAnswer.trim() && formattedText && !isFormatting && !formatError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            Create Golden Answer
          </DialogTitle>
          <DialogDescription>
            Provide the correct answer with sources. It will be automatically formatted for Slack.
          </DialogDescription>
        </DialogHeader>

        {/* Question context */}
        <div className="bg-muted/50 rounded-lg p-3 mb-4">
          <p className="text-sm font-medium text-muted-foreground mb-1">Original Question:</p>
          <p className="text-sm">{conversation.query}</p>
        </div>

        {/* Two-panel layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 overflow-hidden">
          {/* Left: Editor */}
          <div className="flex flex-col min-h-0">
            <h3 className="text-sm font-medium mb-2">Answer Editor</h3>
            <div className="flex-1 overflow-auto">
              <AnswerEditor
                value={rawAnswer}
                onChange={handleAnswerChange}
                placeholder="Enter the correct answer here. Add sources using [Label: URL] format..."
                citations={citations}
                minHeight="250px"
              />
            </div>
          </div>

          {/* Right: Preview */}
          <div className="flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Slack Preview</h3>
              {isFormatting && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Formatting...
                </span>
              )}
            </div>
            <div className="flex-1 overflow-auto rounded-lg border bg-muted/30 p-4">
              {formatError ? (
                <div className="flex items-center gap-2 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {formatError}
                </div>
              ) : (
                <SlackPreview
                  blocks={blocks}
                  formattedText={formattedText}
                  isLoading={isFormatting}
                  botName="OREO"
                />
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!canSave || createMutation.isPending}
            className="gap-2"
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Save Golden Answer
              </>
            )}
          </Button>
        </DialogFooter>

        {createMutation.isError && (
          <p className="text-sm text-destructive mt-2">
            {createMutation.error instanceof Error
              ? createMutation.error.message
              : 'Failed to save golden answer'}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default GoldenAnswerEditorDialog;
